import { err, ok } from 'neverthrow';
import { Constant, Values } from '../../builder/utilities';
import { Relation } from '../../builder/relation';
import { Ty } from '../../ty';
import { Assert } from '../../utils';
import { SpecialForm } from './types';
import { callClosure, compileToExpr, expectRel } from '../eraql-ast/compile';
import _ from 'lodash';
import { AST } from '../../ast';
import {
  Bindings,
  CompileOutput,
  EraQlAst,
  Item,
  ParseError,
  ParseRes,
} from '../eraql-ast/ast';
import { combineRanges, TokenMeta, WithMeta } from '../lex';
import { c } from './combinators';
import { Expression } from '../../builder';

const Values_: SpecialForm = (args, ctx, func) => {
  const [vals] = args;
  Assert.assert(vals !== undefined);
  const rowsRes = c.ListOf(c.RecordOf(c.Scalar))(vals, ctx);

  if (rowsRes.isErr()) {
    return err(rowsRes.error);
  }

  const rows: Ty.Row[] = rowsRes.value.map((row) =>
    _.mapValues(row[0], (val) => val.v[0])
  );
  return tryTypeCheckRel(Values(rows).ast, func[1]);
};

const Summary_: SpecialForm = (args, ctx, op) => {
  const [target, selection] = args;

  Assert.assert(target !== undefined && selection !== undefined);

  const emptyGroupBy: WithMeta<EraQlAst> = [
    { t: 'list', elems: [] },
    { range: null, comments: { leading: [], trailing: null } },
  ];

  return Aggregate_([target, emptyGroupBy, selection], ctx, op);
};

const GenerateSeries_: SpecialForm = (args, ctx, func) =>
  c
    .Args(c.Expr, c.Expr)(args, ctx, func)
    .andThen(([start, stop]) => {
      const rel: AST._GenerateSeries<AST.RelMacroChildren> = {
        t: 'generate-series',
        start: start.ast,
        stop: stop.ast,
      };

      return tryTypeCheckRel(rel, func[1]);
    });

const Aggregate_: SpecialForm = (args, ctx, func) => {
  const [target, groupBy, select, ...rest] = args;
  Assert.assert(
    target !== undefined &&
      groupBy !== undefined &&
      select !== undefined &&
      rest.length === 0,
    'Arity already checked'
  );

  const targetRes = expectRel(target, ctx);

  if (targetRes.isErr()) {
    return err(targetRes.error);
  }

  const groupList = c.ListOf(c.Ident)(groupBy, ctx);

  if (groupList.isErr()) {
    return err(groupList.error);
  }

  const selectionRes = c.RecordOf(c.Expr)(select, {
    ...ctx,
    attributes: targetRes.value.attributes,
  });

  if (selectionRes.isErr()) {
    return err(selectionRes.error);
  }

  const ast: AST._Aggregate<AST.RelMacroChildren> = {
    t: 'aggregate',
    groupedAttributes: groupList.value.map((x) => x[0].name),
    sources: { from: targetRes.value.ast },
    selection: _.mapValues(selectionRes.value, (x) => x.v[0].ast),
  };

  return tryTypeCheckRel(ast, func[1]);
};

const parseSelectOptions = (opts: {
  preAttrs: Record<string, Ty.ExtendedAttributeType | Expression>;
  postattrs: Record<string, Ty.ExtendedAttributeType | Expression>;
}) =>
  c.StructOf({
    required: {},
    optional: {
      distinct: c.BoolLit,
      limit: c.NumLit,
      offset: c.NumLit,
      meta: c.RecordOf(c.StringLit),
      where: (ast, ctx) => c.Expr(ast, { ...ctx, attributes: opts.preAttrs }),
      order_by: (ast, ctx) =>
        c.ListOf(c.OrderBy)(ast, { ...ctx, attributes: opts.postattrs }),
    },
    strict: true,
  });

const From_: SpecialForm = (args, ctx, func) =>
  c
    .Args(
      c.StructOf({
        required: {
          name: c.StringLit,
          schema: c.StringLit,
          attributes: c.RecordOf(c.Ty),
        },
        optional: {},
        strict: true,
      })
    )(args, ctx, func)
    .andThen(([{ name, schema, attributes }]) => {
      const rel: AST._TableContract = {
        t: 'table',
        name: name.v[0],
        schema: schema.v[0],
        attributes: _.mapValues(attributes.v[0], (x) => x.v[0]),
      };

      return tryTypeCheckRel(rel, func[1]);
    });

const Select_: SpecialForm = (args, ctx, func) => {
  const [target, sel, opts] = args;

  Assert.assert(
    target !== undefined && sel !== undefined,
    'Arity already checked'
  );

  const childRel = expectRel(target, ctx);

  if (childRel.isErr()) {
    return err(childRel.error);
  }

  let selection: Record<string, Expression>;

  const selectionRes = c.RecordOf(c.Expr)(sel, {
    ...ctx,
    attributes: childRel.value.attributes,
  });

  if (selectionRes.isOk()) {
    selection = _.mapValues(selectionRes.value, (x) => x.v[0]);
  } else {
    const closureRes = c.Closure({ arity: [0, 1] })(sel, ctx);

    if (closureRes.isErr()) {
      return err(closureRes.error);
    }

    const { closure, bindings } = closureRes.value[0];

    const t: Item = {
      t: 'item-map',
      map: _.mapValues(
        childRel.value.ref('from').star(),
        (expr): CompileOutput => ({ item: { t: 'expr', expr } })
      ),
    };

    const args: WithMeta<CompileOutput>[] = closure.args[0].map(
      ({ sym: [_sym, meta] }) => [{ item: t }, meta]
    );

    const called = callClosure(closure, bindings, args, sel[1]).andThen((x) =>
      c.ItemMap([x.item, sel[1]], ctx)
    );

    if (called.isErr()) {
      return err(called.error);
    } else {
      selection = {};

      for (const [key, expr] of Object.entries(called.value)) {
        const exprRes = c.Expr([expr.item, sel[1]], ctx);

        if (exprRes.isErr()) {
          const e: WithMeta<ParseError> = [
            { t: 'expected', expected: 'expression' },
            sel[1],
          ];

          return err([e]);
        }
        selection[key] = exprRes.value;
      }
    }
  }

  const defaultOpts: WithMeta<EraQlAst> = [
    { t: 'assoc', entries: [] },
    { range: null, comments: { leading: [], trailing: null } },
  ];

  const optionsRes = parseSelectOptions({
    preAttrs: childRel.value.attributes,
    postattrs: _.mapValues(selection, (x) => x.ty),
  })(opts ?? defaultOpts, ctx);
  if (optionsRes.isErr()) {
    return err(optionsRes.error);
  }
  const { distinct, limit, offset, where, meta, order_by } = optionsRes.value;

  const select: AST._Select<AST.RelMacroChildren> = {
    t: 'select',
    distinct: distinct ? distinct.v[0] : false,
    limit: limit ? limit.v[0] : null,
    offset: offset ? offset.v[0] : null,
    sources: { from: childRel.value.ast },
    selection: _.mapValues(selection, (x) => x.ast),
    orderBys: order_by
      ? order_by.v[0].map(([{ expr, direction }, _meta]) => ({
          expr: expr.ast,
          direction,
        }))
      : [],
    condition: where ? where.v[0].ast : null,
    meta: meta ? _.mapValues(meta.v[0], (item) => item.v[0]) : {},
  };

  return tryTypeCheckRel(select, func[1]);
};

const Table_: SpecialForm = (args, ctx, func) => {
  const [target, attrs] = args;

  Assert.assert(
    target !== undefined && attrs !== undefined,
    'Arity already checked'
  );

  const targetRes = c.StructOf({
    required: {
      schema: c.StringLit,
      name: c.StringLit,
    },
    optional: {},
    strict: true,
  })(target, ctx);

  if (targetRes.isErr()) {
    return err(targetRes.error);
  }

  const attrsRes = c.RecordOf(c.Ty)(attrs, ctx);

  if (attrsRes.isErr()) {
    return err(attrsRes.error);
  }

  const rel: AST._TableContract = {
    t: 'table',
    name: targetRes.value.name.v[0],
    schema: targetRes.value.schema.v[0],
    attributes: _.mapValues(attrsRes.value, (x) => x.v[0]),
  };

  return tryTypeCheckRel(rel, func[1]);
};

const Where_: SpecialForm = (args, ctx, func) => {
  const [target, where] = args;
  Assert.assert(target !== undefined && where !== undefined, 'Arity checkd');

  const relRes = expectRel(target, ctx);

  if (relRes.isErr()) {
    return err(relRes.error);
  }

  const condition = compileToExpr(where, {
    ...ctx,
    attributes: relRes.value.attributes,
  });

  if (condition.isErr()) {
    return err(condition.error);
  }

  const t = relRes.value.ref('from');

  const rel: AST._Select<AST.RelMacroChildren> = {
    t: 'select',
    distinct: false,
    limit: null,
    offset: null,
    sources: {
      from: relRes.value.ast,
    },
    selection: _.mapValues(t.star(), (x) => x.ast),
    orderBys: [],
    condition: condition.value.ast,
    meta: {},
  };

  return tryTypeCheckRel(rel, func[1]);
};

const Derive_: SpecialForm = (args, ctx, func) => {
  const [target, next, opts] = args;
  Assert.assert(target !== undefined && next !== undefined, 'Arity checkd');

  const childRel = expectRel(target, ctx);

  if (childRel.isErr()) {
    return err(childRel.error);
  }

  let selection: Record<string, Expression>;

  const selectionRes = c.RecordOf(c.Expr)(next, {
    ...ctx,
    attributes: childRel.value.attributes,
  });

  if (selectionRes.isOk()) {
    selection = _.mapValues(selectionRes.value, (x) => x.v[0]);
  } else {
    return err(selectionRes.error);
  }

  const defaultOpts: WithMeta<EraQlAst> = [
    { t: 'assoc', entries: [] },
    { range: null, comments: { leading: [], trailing: null } },
  ];

  const optionsRes = parseSelectOptions({
    preAttrs: childRel.value.attributes,
    postattrs: _.mapValues(selection, (x) => x.ty),
  })(opts ?? defaultOpts, ctx);

  if (optionsRes.isErr()) {
    return err(optionsRes.error);
  }

  const { distinct, limit, offset, where, order_by, meta } = optionsRes.value;

  const t = childRel.value.ref('from');

  const rel: AST._Select<AST.RelMacroChildren> = {
    t: 'select',
    distinct: distinct ? distinct.v[0] : false,
    limit: limit ? limit.v[0] : null,
    offset: offset ? offset.v[0] : null,
    condition: where ? where.v[0].ast : null,
    sources: {
      from: childRel.value.ast,
    },
    selection: {
      ..._.mapValues(childRel.value.attributes, (_, name) => t.attr(name).ast),
      ..._.mapValues(selection, (x) => x.ast),
    },
    orderBys: order_by
      ? order_by.v[0].map(([{ expr, direction }, _meta]) => ({
          expr: expr.ast,
          direction,
        }))
      : [],
    meta: meta ? _.mapValues(meta.v[0], (item) => item.v[0]) : {},
  };

  return tryTypeCheckRel(rel, func[1]);
};

const UnionAll_: SpecialForm = (args, ctx, func) => {
  const rels: WithMeta<Relation>[] = [];

  for (const arg of args) {
    const relRes = expectRel(arg, ctx);
    if (relRes.isErr()) {
      return err(relRes.error);
    }
    rels.push([relRes.value, arg[1]]);
  }

  const [first, ...rest] = rels;

  if (first === undefined) {
    return err([[{ t: 'invalid-arity' }, func[1]]]);
  }

  let [rel, { range }] = first;

  for (const [next, { range: nextRange }] of rest) {
    range = combineRanges(range, nextRange);

    const union: AST._Union<AST.RelMacroChildren> = {
      t: 'union',
      all: true,
      sources: { left: rel.ast, right: next.ast },
    };

    const unionRes = Relation.tryFromAst(union);

    if (unionRes.isErr()) {
      return err([
        [
          {
            t: 'type-check-error',
            trace: unionRes.error,
          },
          { range },
        ],
      ]);
    }

    rel = unionRes.value;
  }

  return tryTypeCheckRel(rel.ast, func[1]);
};

const InformationSchema_: SpecialForm = (args, ctx, func) => {
  const [target, ...rest] = args;

  Assert.assert(
    target !== undefined && rest.length === 0,
    'Arity already checked'
  );

  const res = c.StructOf({
    required: {
      schemas: c.ListOf(c.StringLit),
      type: c.EnumLit('tables', 'columns'),
    },
    optional: {},
    strict: true,
  })(target, ctx);

  if (res.isErr()) {
    return err(res.error);
  }

  const rel: AST._InformationSchema = {
    t: 'information-schema',
    schemas: res.value.schemas.v[0].map(([schema, _meta]) => schema),
    type: res.value.type.v[0],
  };

  return tryTypeCheckRel(rel, func[1]);
};

const File_: SpecialForm = (args, ctx, func) =>
  c
    .Args(c.StringLit, c.RecordOf(c.Ty))(args, ctx, func)
    .andThen(([uri, attributes]) => {
      const rel: AST._FileContract = {
        t: 'file',
        uri: uri,
        attributes: _.mapValues(attributes, (attr) => attr.v[0]),
      };

      return tryTypeCheckRel(rel, func[1]);
    });

/**
 * Given a relation and a reference direction, produce a symbol map of all of its columns.
 */
const itemMapOfRel = (
  rel: Relation,
  source: 'left' | 'right' | 'from'
): Record<string, CompileOutput> =>
  _.mapValues(
    rel.ref(source).star(),
    (expr): CompileOutput => ({ item: { t: 'expr', expr } })
  );

const Join_ =
  (how: AST.JoinType): SpecialForm =>
  (args, ctx, func) => {
    const [lhs, rhs, join] = args;
    Assert.assert(lhs !== undefined && rhs !== undefined && join !== undefined);

    const rels = c.Args(c.Rel, c.Rel, c.Closure({ arity: [0, 1, 2] }))(
      // TODO spread operator in select: The select version of this will accept both a closure or a struct (later on) for third parameter
      args,
      ctx,
      func
    );

    if (rels.isErr()) {
      return err(rels.error);
    }

    const [lRel, rRel, [closure, _closureMeta]] = rels.value;
    const [[l, r], _argsMeta] = closure.closure.args;

    const bindings: Bindings = {
      ...closure.bindings,
      ...(l
        ? {
            [l.sym[0]]: {
              t: 'item-map',
              map: itemMapOfRel(lRel, 'left'),
            },
          }
        : {}),
      ...(r
        ? {
            [r.sym[0]]: {
              t: 'item-map',
              map: itemMapOfRel(rRel, 'right'),
            },
          }
        : {}),
    };

    const expr = c.StructOf({
      required: {
        on: c.Expr,
        select: c.RecordOf(c.Expr),
      },
      optional: {},
      strict: true,
    })(closure.closure.body, {
      ...ctx,
      bindings,
      attributes: {},
    });

    if (expr.isErr()) {
      return err(expr.error);
    }

    const { on, select } = expr.value;

    const ast: AST._Join<AST.RelMacroChildren> = {
      t: 'join',
      how,
      sources: { left: lRel.ast, right: rRel.ast },
      condition: on.v[0].ast,
      selection: _.mapValues(select.v[0], (expr) => expr.v[0].ast),
    };

    return tryTypeCheckRel(ast, func[1]);
  };

const RelVar_: SpecialForm = (args, ctx, func) =>
  c
    .Args(
      c.StructOf({
        required: {
          name: c.StringLit,
          scope: c.StringLit,
          attributes: c.RecordOf(c.Ty),
        },
        optional: { default: c.Rel },
        strict: true,
      })
    )(args, ctx, func)
    .andThen(
      ([
        { name, scope, attributes, default: default_ },
      ]): ParseRes<CompileOutput> => {
        const rel: AST._RelVar<AST.RelMacroChildren> = {
          t: 'rel-var',
          name: name.v[0],
          scope: scope.v[0],
          attributes: _.mapValues(attributes.v[0], (attr) => attr.v[0]),
          default: default_ ? default_.v[0].ast : null,
        };

        return tryTypeCheckRel(rel, func[1]);
      }
    );

const ColumnsOf_: SpecialForm = (args, ctx, func) =>
  c
    .Args(c.Rel)(args, ctx, func)
    .map(([rel]): CompileOutput => {
      const cols = Object.entries(rel.attributes).map(([name, type]) => ({
        name,
        type: Ty.displayTy(type),
        nullable: type.nullable,
      }));

      const res = Values(cols, {
        name: Ty.nn('string'),
        type: Ty.nn('string'),
        nullable: Ty.nn('boolean'),
      });

      const output: CompileOutput = {
        item: { t: 'rel', rel: res },
      };

      return output;
    });

const TypeOfColumn_: SpecialForm = (args, ctx, func) =>
  c
    .Args(c.Rel, c.IncludeMeta(c.StringLit))(args, ctx, func)
    .andThen(([rel, [name, nameMeta]]): ParseRes<CompileOutput> => {
      const ty = rel.attributes[name];

      if (ty === undefined) {
        const e: ParseError = {
          t: 'attribute-not-found',
          name,
        };
        return err([[e, nameMeta]]);
      }

      const output: CompileOutput = {
        item: { t: 'expr', expr: Constant(Ty.displayTy(ty)) },
      };
      return ok(output);
    });

const VariantsOfEnum_: SpecialForm = (args, ctx, func) =>
  c
    .Args(c.IncludeMeta(c.Ty))(args, ctx, func)
    .andThen(([[ty, tyMeta]]): ParseRes<CompileOutput> => {
      if (ty.ty.k !== 'enum') {
        const e: ParseError = {
          t: 'expected',
          expected: 'enum',
        };
        return err([[e, tyMeta]]);
      }
      const vals = Values(
        ty.ty.t.map((variant) => ({ variant })),
        { variant: Ty.nn('string') }
      );

      const output: CompileOutput = {
        item: { t: 'rel', rel: vals },
      };

      return ok(output);
    });

const tryTypeCheckRel = (
  rel: AST.RelFR,
  meta: TokenMeta
): ParseRes<CompileOutput> => {
  const res = Relation.tryFromAst(rel);

  if (res.isErr()) {
    const e: WithMeta<ParseError> = [
      { t: 'type-check-error', trace: res.error },
      meta,
    ];
    return err([e]);
  }

  return ok({ item: { t: 'rel', rel: res.value }, bindings: {} });
};

const Limit_: SpecialForm = (...args) =>
  c
    .Args(
      c.Rel,
      c.NumLit
    )(...args)
    .map(
      ([rel, limit]): CompileOutput => ({
        item: { t: 'rel', rel: rel.limit(limit) },
      })
    );

const Distinct_: SpecialForm = (...args) =>
  c
    .Args(c.Rel)(...args)
    .map(([rel]) => ({
      item: { t: 'rel', rel: rel.distinct() },
    }));

export const REL_SPECIAL_FORMS: Record<
  string,
  [arity: number | number[] | 'any', fn: SpecialForm]
> = {
  From: [1, From_],
  Values: [1, Values_],
  Limit: [2, Limit_],
  Select: [[2, 3], Select_],
  Where: [2, Where_],
  Aggregate: [3, Aggregate_],
  Summary: [2, Summary_],
  GenerateSeries: [2, GenerateSeries_],
  Table: [2, Table_],
  UnionAll: ['any', UnionAll_],
  Derive: [[2, 3], Derive_],
  InformationSchema: [1, InformationSchema_],
  RelVar: [[1], RelVar_],
  File: [2, File_],
  LeftJoin: [3, Join_('left')],
  RightJoin: [3, Join_('right')],
  InnerJoin: [3, Join_('inner')],
  ColumnsOf: [1, ColumnsOf_],
  TypeOfColumn: [2, TypeOfColumn_],
  VariantsOfEnum: [1, VariantsOfEnum_],
  Distinct: [1, Distinct_],
};
